import type { Ref } from 'react'
import type { DeepPartial } from '@apollo/client/utilities'
import { Flex } from '@weareberyl/design-system'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Form, Input, Card, Select, Button, FormInstance } from 'antd'

import type { DetailsFormValues } from './formTypes'
import { layout } from '~/constants/forms'
import {
  ProductImageVariant,
  SchemeProductInput,
} from '~/gql/generated/graphql'
import { periodPassImages } from './List/ProductDetails'

type Props = {
  loading?: boolean
  initialValues?: DeepPartial<SchemeProductInput>
  formRef: Ref<FormInstance<DetailsFormValues>>
}
const PeriodPassDetailsForm = ({ loading, initialValues, formRef }: Props) => {
  const [form] = Form.useForm()
  const imageVariant = Form.useWatch(['details', 'image_variant'], form)

  return (
    <Form<DetailsFormValues>
      {...layout}
      wrapperCol={{ span: 12 }}
      labelCol={{ span: 8 }}
      layout="horizontal"
      form={form}
      name="details"
      initialValues={initialValues}
      disabled={loading}
      ref={formRef}
    >
      <Card title="Period pass info">
        <Form.Item
          name={['details', 'title']}
          label="Title"
          rules={[{ required: true, message: 'Please specify a title' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['details', 'sub_title']}
          label="Subtitle"
          rules={[{ required: true, message: 'Please specify a subtitle' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['details', 'benefit_sentence']}
          label="Benefit sentence"
          rules={[
            { required: true, message: 'Please specify a benefit sentence' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['details', 'cta_text']}
          label="CTA text"
          rules={[{ required: true, message: 'Please specify some CTA text' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['details', 'image_variant']}
          label="Image variant"
          rules={[
            { required: true, message: 'Please choose an image variant' },
          ]}
        >
          <Form.Item noStyle name={['details', 'image_variant']}>
            <Select
              options={Object.keys(ProductImageVariant).map(value => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>

          <Flex mt={2}>
            <img
              width={150}
              src={periodPassImages[imageVariant]}
              alt={imageVariant}
            />
          </Flex>
        </Form.Item>
        <Form.List name={['details', 'bullets']}>
          {(fields, { remove, add }, { errors }) => (
            <>
              {fields.map((fieldProps, index) => (
                <Form.Item
                  {...fieldProps}
                  label={index === 0 ? 'Bullets' : ''}
                  wrapperCol={
                    index > 0 ? { offset: 8, span: 13 } : { span: 13 }
                  }
                  required={false}
                  key={fieldProps.key}
                >
                  <Form.Item
                    {...fieldProps}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message:
                          'Please write something in or delete this field.',
                      },
                    ]}
                    noStyle
                  >
                    <Input
                      style={{ width: 'calc(100% - 30px)', marginRight: 10 }}
                    />
                  </Form.Item>
                  <MinusCircleOutlined
                    onClick={() => remove(fieldProps.name)}
                  />
                </Form.Item>
              ))}
              <Form.Item wrapperCol={{ offset: 8 }}>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  {fields.length > 0
                    ? 'Add another bullet point'
                    : 'Add a bullet point'}
                </Button>
              </Form.Item>

              <Form.ErrorList errors={errors} />
            </>
          )}
        </Form.List>
      </Card>
    </Form>
  )
}

export default PeriodPassDetailsForm
